// The /policies route table (policies-ux, brief §1b-3) — registered ONCE by U0 and
// spread into `routes/index.tsx`. Every surface is its own lazy module; U1–U4
// replace only their module body (`map/`, `simulator/`, `compare/`, `wizard/`),
// never this table or the router.
//
// ORDER MATTERS: every literal segment (`new`, `simulator`, `explorer`, `compare`)
// is declared before `policies/:policyUrn`, so none is ever matched as a URN.
// `/policies/:policyUrn` itself is unchanged — ~10 deep links point at it.
//
// A plain `.ts` module (createElement, no JSX) so the table is not a component
// file: react-refresh's only-export-components rule applies to `.tsx` only.

import { createElement, lazy, type ReactNode } from 'react';
import type { RouteObject } from 'react-router';
import type { PermissionKey } from '@nexa/auth';
import { NAV_PERMISSIONS as NAV } from '@/lib/access/route-permissions';
import { ExplorerRedirect } from './explorer-redirect';

const PoliciesMapRoute = lazy(() => import('../map'));
const PolicySimulatorRoute = lazy(() => import('../simulator'));
const PolicyCompareRoute = lazy(() => import('../compare'));
const PolicyWizardRoute = lazy(() => import('../wizard'));
const PolicyDetailRoute = lazy(() =>
  import('../policy-detail').then((m) => ({ default: m.PolicyDetailRoute })),
);

/** The router's own wrappers, passed in so the table gates and suspends exactly like every other route. */
export interface IPolicyRouteWrappers {
  guard: (permission: PermissionKey, el: ReactNode) => ReactNode;
  lazyRoute: (el: ReactNode) => ReactNode;
}

export function policyRoutes({ guard, lazyRoute }: IPolicyRouteWrappers): RouteObject[] {
  const view = (el: ReactNode) => guard(NAV.policies, lazyRoute(el));
  const edit = (el: ReactNode) => guard('policies.edit', lazyRoute(el));
  return [
    // Policies per docs/ui/operator-ui-tds.md §4.1 + §8 (a1, a4, a5) — policies-ux surfaces.
    { path: 'policies', element: view(createElement(PoliciesMapRoute)) },
    { path: 'policies/new', element: edit(createElement(PolicyWizardRoute, { mode: 'create' })) },
    { path: 'policies/simulator', element: view(createElement(PolicySimulatorRoute)) },
    // The former explorer — kept as a redirect so old links land on the Simulador.
    { path: 'policies/explorer', element: guard(NAV.policies, createElement(ExplorerRedirect)) },
    { path: 'policies/compare', element: view(createElement(PolicyCompareRoute)) },
    {
      path: 'policies/:policyUrn/edit',
      element: edit(createElement(PolicyWizardRoute, { mode: 'edit' })),
    },
    { path: 'policies/:policyUrn', element: view(createElement(PolicyDetailRoute)) },
  ];
}
